import React, { forwardRef, useRef } from 'react';
import { Icon, Input, Popup, Search } from 'semantic-ui-react';
import NavBarSearchProviders from './NavBarSearchProviders';
import {
  deleteSearchCompletion,
  getSearchCompletion,
  getDownloadHistory,
} from '../../services/Search';
import { torrentRedownload } from '../../services/Torrent';
import { notifError, notifSuccess } from '../Tools/NotificationToasts';
import { getLocalServerConfig } from '../../services/Server';

const NavBarSearchInput = forwardRef(
  (
    {
      setSearchInput,
      providers,
      selectedProvider,
      getProvidersList,
      providersUpdatedAt,
      providersLoading,
      handleChangeProvider,
      refreshProviders,
      handleTabChange,
      searchCallback,
      searchInput,
      mobile = false,
    },
    ref
  ) => {
    const [state, setState] = React.useState({
      completion: [],
      history: [],
      loading: false,
      loadingHistory: false,
    });

    const getDisplayName = (file) => {
      if (!file) return file;
      const magnetMatch = file.match(/[?&]dn=([^&]+)/);
      if (magnetMatch) {
        try {
          const cleaned = magnetMatch[1].replace(/\.torrent$/i, '').replace(/%[0-9a-fA-F]?$/, '');
          return decodeURIComponent(cleaned.replace(/\+/g, ' '));
        } catch (_) {
          return file;
        }
      }
      return file.replace(/\.torrent$/i, '');
    };

    const getHistory = (query) => {
      setState((prevState) => ({ ...prevState, loadingHistory: true }));
      getDownloadHistory(query)
        .then((response) => {
          if (response.status === 200 && response.data.success) {
            setState((prevState) => ({
              ...prevState,
              loadingHistory: false,
              history: response.data.result
                .filter(
                  (res, index, arr) =>
                    !res.hash || arr.findIndex((r) => r.hash === res.hash) === index
                )
                .map((res) => ({
                  title: getDisplayName(res.file),
                  file: res.file,
                  magnet: res.magnet,
                  torrent_path: res.torrent_path,
                  downloader_id: res.downloader_id,
                  hash: res.hash,
                  user: res.user,
                  updated_at: res.updated_at,
                  type: 'download',
                })),
            }));
          }
        })
        .catch((e) => {
          console.log(`Échec de récupération d'historique: ${e}`);
        })
        .finally(() => {
          setState((prevState) => ({ ...prevState, loadingHistory: false }));
        });
    };

    const getCompletion = (query) => {
      setState((prevState) => ({ ...prevState, loading: true }));
      getSearchCompletion(query)
        .then((response) => {
          if (response.status === 200 && response.data.success) {
            setState((prevState) => ({
              ...prevState,
              loading: false,
              completion: response.data.result.map((res) => ({
                title: res.query,
                type: 'completion',
              })),
            }));
          }
        })
        .catch((e) => {
          console.log(`Échec d'autocompletion: ${e}`);
        })
        .finally(() => {
          setState((prevState) => ({ ...prevState, loading: false }));
        });
    };

    const deleteSuggestion = (query) => {
      deleteSearchCompletion(query)
        .then((response) => {
          if (response.status === 200 && response.data.success) {
            notifSuccess(`Suggestion supprimée: ${query}`);
            setState((prevState) => ({
              ...prevState,
              completion: prevState.completion.filter((c) => c.title !== query),
            }));
          } else notifError(`Échec de la suppression`);
        })
        .catch((e) => {
          console.log(`Échec de la suppression: ${e}`);
        });
    };

    const handleRedownload = (item) => {
      torrentRedownload({ downloader_id: item.downloader_id, file: item.file })
        .then((response) => {
          if (response.status === 200 && response.data.success) {
            notifSuccess(`Retéléchargement lancé: ${item.file}`);
          } else notifError(`Échec du retéléchargement`);
        })
        .catch((e) => {
          console.log(`Échec du retéléchargement: ${e}`);
          notifError(`Échec du retéléchargement`);
        });
    };

    const completionRenderer = ({ title }) => (
      <div style={{ width: '100%' }}>
        <div
          style={{ float: 'left', position: 'relative', width: '95%' }}
          className="font-weight-bolder"
        >
          {title}
        </div>
        <div style={{ float: 'left', position: 'relative', width: '5%' }}>
          <Popup
            content="Supprimer la suggestion"
            position="bottom center"
            align="right"
            trigger={
              <Icon
                style={{ opacity: 0.2 }}
                name="delete"
                color="grey"
                onClick={(e) => {
                  e.stopPropagation();
                  deleteSuggestion(title);
                }}
              />
            }
          />
        </div>
      </div>
    );

    const getDaysAgo = (updatedAt) => {
      if (!updatedAt) return null;
      const days = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 86400000);
      return days;
    };

    const downloadRenderer = ({ title, magnet, torrent_path, downloader_id, file, updated_at }) => {
      const canRedownload = !!(magnet || torrent_path);
      return (
        <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
          {canRedownload ? (
            <Popup
              content={`Télécharger vers ${getLocalServerConfig().siteName || 'Ergo'}`}
              position="bottom center"
              trigger={
                <Icon
                  style={{ marginRight: '8px' }}
                  size="large"
                  name="download"
                  color="teal"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRedownload({ downloader_id, file, magnet, torrent_path });
                  }}
                />
              }
            />
          ) : (
            <Popup
              content="Recherche"
              position="bottom center"
              trigger={
                <Icon
                  style={{ marginRight: '8px' }}
                  size="small"
                  name="search"
                  color="grey"
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                />
              }
            />
          )}
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
            className="font-weight-bolder"
          >
            {title}
          </div>
          <span
            style={{
              opacity: 0.4,
              marginLeft: '8px',
              flexShrink: 0,
              fontSize: '0.8em',
              whiteSpace: 'nowrap',
            }}
          >
            <Icon name="clock outline" size="small" />
            {getDaysAgo(updated_at)} j
          </span>
          <Icon
            style={{ opacity: 0.3, marginLeft: '6px', flexShrink: 0 }}
            name={magnet ? 'magnet' : 'file alternate outline'}
            size="small"
          />
        </div>
      );
    };

    const resultRenderer = (item) => {
      if (item.type === 'download') return downloadRenderer(item);
      return completionRenderer(item);
    };

    const categoryLayoutRenderer = ({ categoryContent, resultsContent }) => (
      <div style={{ width: '100%' }}>
        <div
          style={{
            padding: '6px 14px 4px',
            fontSize: '0.78em',
            fontWeight: 'bold',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#aaa',
            borderBottom: '1px solid #f0f0f0',
          }}
        >
          {categoryContent}
        </div>
        {resultsContent}
      </div>
    );

    const buildResults = () => {
      const results = {};
      if (state.completion.length > 0) {
        results.searchHistory = {
          name: 'Historique de mes recherches',
          results: state.completion,
        };
      }
      if (state.history.length > 0) {
        results.downloadHistory = {
          name: 'Historique de tous les téléchargements',
          results: state.history,
        };
      }
      return results;
    };

    const debounceRef = useRef(null);

    const handleCompletion = ({ target }) => {
      const { value } = target;
      setSearchInput(value);
      clearTimeout(debounceRef.current);
      if (value) {
        debounceRef.current = setTimeout(() => {
          getHistory(value);
          getCompletion(value);
        }, 200);
      } else
        setState((prevState) => ({
          ...prevState,
          completion: [],
          history: [],
        }));
    };

    const handleSearch = (event) => {
      if (event.key === 'Enter' && event.target.value.length > 1) {
        event.target.blur();
        searchCallback(event.target.value);
      }
    };

    return (
      <Search
        style={{ width: mobile ? '100%' : '60%' }}
        showNoResults={false}
        onSearchChange={handleCompletion}
        minCharacters={1}
        results={buildResults()}
        loading={state.loading || state.loadingHistory}
        resultRenderer={resultRenderer}
        categoryLayoutRenderer={categoryLayoutRenderer}
        category
        onResultSelect={(e, data) => setSearchInput(data.result.title)}
        onSelectionChange={(e, data) => setSearchInput(data.result.title)}
        input={
          <Input
            ref={ref}
            fluid
            action={
              <NavBarSearchProviders
                providers={providers}
                selectedProvider={selectedProvider}
                getProvidersList={getProvidersList}
                providersUpdatedAt={providersUpdatedAt}
                providersLoading={providersLoading}
                handleChangeProvider={handleChangeProvider}
                refreshProviders={refreshProviders}
              />
            }
            autoComplete="off"
            size="large"
            iconPosition="left"
            icon="search"
            type="text"
            onClick={() => {
              handleTabChange('', 'search');
            }}
            onKeyUp={handleSearch}
            placeholder="Recherche..."
            value={searchInput}
          />
        }
      />
    );
  }
);

export default NavBarSearchInput;
